import React, { useMemo, useCallback } from 'react';
import type { FC } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';

import { CommentNavigation as CommentNavigationCommon } from '@confluence/inline-comments-common/entry-points/CommentNavigation.ts';
import type { Comment } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import {
	useAnnotationsDispatch,
	useAnnotations,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';

import { useInlineCommentNavigation } from './hooks/useInlineCommentNavigation.ts';

type CommentNavigationProps = {
	pageId: string;
	commentData: Comment;
	isEditor?: boolean;
	onNavigationClick: (nextMarkerRef: string) => void;
};

const positionLeftStyles = xcss({
	position: 'absolute',
	top: 'space.150',
	left: 'space.150',
});

export const CommentNavigation: FC<CommentNavigationProps> = ({
	pageId,
	commentData,
	isEditor,
	onNavigationClick,
}) => {
	const { clearStoredCommentData } = useAnnotationsDispatch();
	const { storedCommentData } = useAnnotations();

	const { goToNextInlineComment: originalGoToNext, goToPrevInlineComment: originalGoToPrev } =
		useInlineCommentNavigation({
			isEditor,
			pageId,
			onNavigationClick,
			commentData,
		});

	// Wrap navigation functions to clear stored comment data before navigation
	const goToNextInlineComment = useCallback(() => {
		if (clearStoredCommentData && storedCommentData) {
			clearStoredCommentData();
		}
		originalGoToNext();
	}, [clearStoredCommentData, storedCommentData, originalGoToNext]);

	const goToPrevInlineComment = useCallback(() => {
		if (clearStoredCommentData && storedCommentData) {
			clearStoredCommentData();
		}
		originalGoToPrev();
	}, [clearStoredCommentData, storedCommentData, originalGoToPrev]);

	return useMemo(
		() => (
			<Inline xcss={positionLeftStyles}>
				<CommentNavigationCommon
					goToNextInlineComment={goToNextInlineComment}
					goToPrevInlineComment={goToPrevInlineComment}
				/>
			</Inline>
		),
		[goToNextInlineComment, goToPrevInlineComment],
	);
};
